import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { OnInit } from '@angular/core';
import { Component, inject, Injector, signal } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxFormMode,
  OnyxModalComponent,
  OnyxToastService,
} from '@onyx/angular';
import { Subject, switchMap } from 'rxjs';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { DictionariesNamesTranslationFormComponent } from '../../../common/components/forms/dictionaries-names-translation-form/dictionaries-names-translation-form.component';
import { CompanyDictionaryCode } from '../../../common/enums/company-dictionary-code';
import { DictionariesHelper } from '../../../common/helpers/dictionaries.helper';
import type { CompanyVehicleParameter } from '../../../common/interfaces/company-vehicle-parameter';
import { CompanyDictionariesService } from '../../../common/services/company-dictionaries.service';

export type DictionariesVehicleParameterForm = ReturnType<
  ReturnType<
    DictionariesVehicleParameterModalComponent['buildForm']
  >['getRawValue']
>;

export type DictionariesVehicleParameterModalData =
  | {
      mode: OnyxFormMode.ADD;
      name?: string;
    }
  | {
      mode: OnyxFormMode.EDIT;
      vehicleParameter: CompanyVehicleParameter;
    };

@Component({
  selector: 'app-dictionaries-vehicle-parameter-modal',
  imports: [
    OnyxModalComponent,
    DictionariesNamesTranslationFormComponent,
    OnyxButtonComponent,
    TranslatePipe,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './dictionaries-vehicle-parameter-modal.component.html',
  styleUrl: './dictionaries-vehicle-parameter-modal.component.scss',
})
export class DictionariesVehicleParameterModalComponent implements OnInit {
  protected readonly data =
    inject<DictionariesVehicleParameterModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly dictionariesHelper = inject(DictionariesHelper);

  protected readonly I18N = 'dictionaries';
  protected readonly OnyxFormMode = OnyxFormMode;

  protected form = this.buildForm();
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  public ngOnInit(): void {
    if (this.data.mode === OnyxFormMode.EDIT) {
      this.form.patchValue(this.data.vehicleParameter);
    }
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();

    let action$;
    switch (this.data.mode) {
      case OnyxFormMode.ADD:
        action$ = this.companyDictionariesService.addDictionaryItem(
          CompanyDictionaryCode.VEHICLE_PARAMETER,
          form,
        );
        break;

      case OnyxFormMode.EDIT:
        {
          const { vehicleParameter } = this.data;
          action$ = this.dictionariesHelper
            .confirmEdit(
              CompanyDictionaryCode.VEHICLE_PARAMETER,
              vehicleParameter,
              this.close$,
            )
            .pipe(
              switchMap(() =>
                this.companyDictionariesService.editDictionaryItem(
                  vehicleParameter.uuid,
                  form,
                ),
              ),
            );
        }
        break;
    }

    this.loading.set(true);
    action$
      .subscribe({
        next: () => {
          this.toastService.showSuccess({
            message:
              this.data.mode === OnyxFormMode.ADD
                ? `${this.I18N}.dictionaryAdded`
                : `${this.I18N}.dictionaryEdited`,
          });
          this.close$.next();
        },
        error: (response) =>
          this.dictionariesHelper.handleEditError(
            response,
            CompanyDictionaryCode.VEHICLE_PARAMETER,
            form,
          ),
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      names: DictionariesNamesTranslationFormComponent.buildFom(this.fb),
    });
  }
}
